import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiClient } from '../../lib/api';

// ── Types ──────────────────────────────────────────────────────────────────

export type AdminPermission = {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  accessLevel: 'store' | 'group';
  expiresAt: string | null;
  group: { id: string; slug: string; name: string } | null;
  role?: string;
  scopeType?: string;
  grantedBy?: string;
  grantedAt?: string;
  featureScope?: Array<'parts' | 'chemicals'>;
};

export type AdminUser = {
  id: string;
  email: string | null;
  status: string;
  lastSignInAt: string | null;
  permissions: AdminPermission[];
  roles: {
    tenantId: string;
    tenantSlug: string;
    tenantName: string;
    role: string;
  }[];
};

export type AdminFeature = 'parts' | 'chemicals';

export type AdminStore = {
  id: string;
  slug: string;
  name: string;
  isActive: boolean;
  features: AdminFeature[];
  group: { id: string; slug: string; name: string } | null;
  integrations: any[];
};

export type AdminGroup = {
  id: string;
  name: string;
  slug: string;
  tenantCount: number;
  isActive?: boolean;
  parentGroup: { id: string; name: string; slug: string } | null;
};

interface AdminContextType {
  // Data
  users: AdminUser[];
  stores: AdminStore[];
  groups: AdminGroup[];

  // Loading / error
  usersLoading: boolean;
  storesLoading: boolean;
  groupsLoading: boolean;
  usersError: string | null;
  storesError: string | null;
  groupsError: string | null;

  // Refetch helpers (call after mutations)
  refetchUsers: () => Promise<void>;
  refetchStores: () => Promise<void>;
  refetchGroups: () => Promise<void>;
  refetchAll: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stores, setStores] = useState<AdminStore[]>([]);
  const [groups, setGroups] = useState<AdminGroup[]>([]);

  const [usersLoading, setUsersLoading] = useState(true);
  const [storesLoading, setStoresLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);

  const [usersError, setUsersError] = useState<string | null>(null);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [groupsError, setGroupsError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const res = await apiClient.get('/api/admin/users');
      if (!res.ok) throw new Error(`Failed to load users (${res.status})`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setUsersError(err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      setStoresLoading(true);
      setStoresError(null);
      const res = await apiClient.get('/api/admin/stores');
      if (!res.ok) throw new Error(`Failed to load stores (${res.status})`);
      const data = await res.json();
      setStores(
        Array.isArray(data)
          ? data.map((s: any) => ({
              ...s,
              isActive: s.isActive ?? true,
              features: Array.isArray(s.features) ? s.features : [],
              integrations: s.integrations ?? [],
            }))
          : [],
      );
    } catch (err: any) {
      setStoresError(err.message || 'Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  }, []);

  const fetchGroups = useCallback(async () => {
    try {
      setGroupsLoading(true);
      setGroupsError(null);
      const res = await apiClient.get('/api/admin/groups');
      if (!res.ok) throw new Error(`Failed to load groups (${res.status})`);
      const data = await res.json();
      setGroups(Array.isArray(data?.groups) ? data.groups : Array.isArray(data) ? data : []);
    } catch (err: any) {
      setGroupsError(err.message || 'Failed to load groups');
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const refetchAll = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchStores(), fetchGroups()]);
  }, [fetchUsers, fetchStores, fetchGroups]);

  // Eager-load all three on mount
  useEffect(() => {
    fetchUsers();
    fetchStores();
    fetchGroups();
  }, [fetchUsers, fetchStores, fetchGroups]);

  const value: AdminContextType = {
    users,
    stores,
    groups,
    usersLoading,
    storesLoading,
    groupsLoading,
    usersError,
    storesError,
    groupsError,
    refetchUsers: fetchUsers,
    refetchStores: fetchStores,
    refetchGroups: fetchGroups,
    refetchAll,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

// ── Hooks ──────────────────────────────────────────────────────────────────

function useAdminContext() {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error('useAdminContext must be used within <AdminProvider>');
  return ctx;
}

/** Users list + loading/error + refetch */
export function useAdminUsers() {
  const { users, usersLoading: loading, usersError: error, refetchUsers: refetch } = useAdminContext();
  return { users, loading, error, refetch };
}

/** Stores list + loading/error + refetch */
export function useAdminStores() {
  const { stores, storesLoading: loading, storesError: error, refetchStores: refetch } = useAdminContext();
  return { stores, loading, error, refetch };
}

/** Groups list + loading/error + refetch */
export function useAdminGroups() {
  const { groups, groupsLoading: loading, groupsError: error, refetchGroups: refetch } = useAdminContext();
  return { groups, loading, error, refetch };
}

/** Refetch everything (call after cross-cutting mutations like invite) */
export function useAdminRefetchAll() {
  const { refetchAll } = useAdminContext();
  return refetchAll;
}
